import { MAX_NAME_CHARACTERS, PIX_KEY_MAX_LENGTH, PIX_KEY_MIN_LENGTH } from 'common/constants';
import { cpf as CPF } from 'cpf-cnpj-validator';
import { cnpjValidator } from 'utils/cnpj-validator';
import { cpfValidator } from 'utils/cpf-validator';
import { emailValidator } from 'utils/email-validator';
import { isPhone } from 'utils/phone-number-validator';
import { isUuidV4 } from 'utils/uuid-validator';
import * as yup from 'yup';

const regexJustLetters = /^([A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ]+( [A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ]+)*)?$/;

const regexAtLeastTwoWords = /^\p{L}+(?:\s+\p{L}+)+$/u;

export const createSellerSchema = (hasBankData: boolean) =>
  yup.object().shape({
    code: yup
      .string()
      .required('Informe seu CPF')
      .matches(/^\d{3}\.?\d{3}\.?\d{3}-?\d{2}/, 'CPF inválido')
      .test('custom-cpf-validation', 'CPF inválido', (cpf) => {
        const cpfNumber = cpf?.replace(/[.-]/g, '');
        return CPF.isValid(cpfNumber);
      })
      .typeError('O campo CPF é obrigatório'),

    name: yup
      .string()
      .required('Informe seu nome completo.')
      .trim()
      .matches(regexJustLetters, 'Este campo deve conter somente letras.')
      .max(MAX_NAME_CHARACTERS, `O nome deve ter no máximo ${MAX_NAME_CHARACTERS} caracteres.`)
      .matches(regexAtLeastTwoWords, 'Informe pelo menos nome e sobrenome.'),

    storeName: yup.string().required(),
    profileId: yup.string().optional(),

    phoneNumber: yup.string().when([], {
      is: () => hasBankData,
      then: (schema) =>
        schema.required('Número obrigatório').test('phone-format', 'Número de celular inválido', (value) => {
          if (!value) return false;
          const onlyDigits = value.replace(/\D/g, '');
          return isPhone(onlyDigits);
        }),
      otherwise: (schema) => schema.optional().nullable(),
    }),

    email: yup.string().when([], {
      is: () => hasBankData,
      then: (schema) =>
        schema
          .required('E-mail é obrigatório')
          .test('email-format', 'E-mail inválido', (value) => !!value && emailValidator(value)),
      otherwise: (schema) => schema.optional().nullable(),
    }),

    role: yup.string().when([], {
      is: () => hasBankData,
      then: (schema) => schema.required('Cargo é obrigatório'),
      otherwise: (schema) => schema.optional().nullable(),
    }),

    pixKey: yup.string().when([], {
      is: () => hasBankData,
      then: (schema) =>
        schema
          .required('Chave PIX é obrigatória')
          .matches(/^\S(.*\S)?$/, 'Chave Pix não deve conter espaços em branco')
          .min(PIX_KEY_MIN_LENGTH, `A chave Pix deve ter no mínimo ${PIX_KEY_MIN_LENGTH} caracteres`)
          .max(PIX_KEY_MAX_LENGTH, `O campo chave PIX deve ter no máximo ${PIX_KEY_MAX_LENGTH} caracteres`)
          .test('pix-key-type', 'Chave Pix inválida. Use CPF, CNPJ, telefone, e-mail ou chave aleatória.', (value) => {
            if (!hasBankData) return true;
            if (!value) return false;

            const cleanCnpj = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (cnpjValidator(cleanCnpj)) return true;

            const onlyDigits = value.replace(/\D/g, '');

            if (cpfValidator(onlyDigits)) return true;
            if (isPhone(onlyDigits)) return true;
            if (isUuidV4(value)) return true;
            if (emailValidator(value)) return true;

            return false;
          }),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  });
